import { MdAdd, MdSave } from 'react-icons/md'
import { formatCentsLabel } from '../scheduling-format'

interface SchedulingActionsProps {
    isEditing: boolean
    isBudgetMode: boolean
    isTeleconsultation: boolean
    loading: boolean
    /** Sem local confirmado o salvamento fica travado. */
    addressConfirmed: boolean
    /** Este atendimento vai somar numa cobrança já aberta. */
    grouping: boolean
    /** Cobrança que recebe a soma, quando existe. */
    billingTargetId: number | null
    groupedCount: number
    /** Já lançado na cobrança do grupo, em centavos. */
    groupedAmount: number
    /** Quanto este atendimento acrescenta, em centavos. */
    summaryPrice: number
    /** "paciente", "cliente", "tutor"… conforme o segmento. */
    clientLabel: string
    onCancel: () => void
    /** Marca que o envio é o "Adicionar outro" (ou o desmarca). */
    onAddAnotherToggle: (addAnother: boolean) => void
    /** Sai do agrupamento: este atendimento é cobrado à parte. */
    onDismissGrouping: () => void
}

/**
 * Rodapé do formulário: resumo do que será cobrado e os botões de salvar.
 *
 * Os dois botões são `submit` do formulário da página — a diferença entre eles é
 * apenas a marca de "adicionar outro", que a página lê no envio.
 */
export default function SchedulingActions({
    isEditing,
    isBudgetMode,
    isTeleconsultation,
    loading,
    addressConfirmed,
    grouping,
    billingTargetId,
    groupedCount,
    groupedAmount,
    summaryPrice,
    clientLabel,
    onCancel,
    onAddAnotherToggle,
    onDismissGrouping,
}: SchedulingActionsProps) {
    return (
        <div className="flex flex-col gap-3 rounded-2xl border border-base-300 bg-base-100 p-4">
            {/* Abaixo dos botões e ocupando a linha inteira: em tela pequena,
                dividir a barra com eles espremia o texto a ponto de quebrar
                palavra por palavra. */}
            {/* Só na criação: em edição, o atendimento aberto JÁ está dentro de
                `groupedAmount` (a cobrança é dele), e somar `summaryPrice` por
                cima mostraria um total dobrado. Lá o agrupamento aparece pelo
                botão "Adicionar outro", não por este resumo. */}
            {grouping && !isEditing ? (
                /* Resumo do pagamento agrupado.
                 *
                 * O operador precisa saber QUANTO o paciente vai pagar ao
                 * finalizar — não o número da cobrança, que não diz nada a ele.
                 * Por isso as três linhas: o que já foi lançado, o que este
                 * atendimento acrescenta e o total. O #id fica discreto, só para
                 * achar a cobrança depois. */
                <div className="w-full rounded-xl border border-secondary/30 bg-secondary/5 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-secondary">
                            Resumo
                        </span>
                        <span className="text-[11px] text-base-content/40">
                            cobrança #{billingTargetId}
                        </span>
                    </div>

                    <dl className="flex flex-col gap-1 text-xs">
                        <div className="flex items-center justify-between gap-3">
                            <dt className="text-base-content/60">
                                {groupedCount}{' '}
                                {groupedCount === 1
                                    ? 'agendamento já lançado'
                                    : 'agendamentos já lançados'}
                            </dt>
                            <dd className="tabular-nums text-base-content/80">
                                {formatCentsLabel(groupedAmount)}
                            </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <dt className="text-base-content/60">
                                Este agendamento
                            </dt>
                            <dd className="tabular-nums text-base-content/80">
                                {formatCentsLabel(summaryPrice)}
                            </dd>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-3 border-t border-secondary/20 pt-1.5">
                            <dt className="font-semibold text-base-content">
                                Total a pagar
                            </dt>
                            <dd className="text-sm font-bold tabular-nums text-secondary">
                                {formatCentsLabel(groupedAmount + summaryPrice)}
                            </dd>
                        </div>
                    </dl>

                    <div className="mt-2 flex items-center justify-between gap-2 border-t border-secondary/20 pt-2">
                        <span className="text-[11px] text-base-content/50">
                            "Finalizar" leva ao pagamento do total.
                        </span>
                        {/* Saída obrigatória: a detecção é automática, e o
                            paciente que volta à tarde para algo que quer pagar à
                            parte não pode ficar preso à cobrança da manhã. */}
                        <button
                            type="button"
                            className="btn btn-ghost btn-xs whitespace-nowrap"
                            onClick={onDismissGrouping}
                        >
                            Cobrar à parte
                        </button>
                    </div>
                </div>
            ) : isEditing && billingTargetId != null ? (
                /* Em edição não cabe o resumo com "total a pagar": este
                   atendimento JÁ está dentro da cobrança, e somar o valor dele de
                   novo mostraria o dobro. Mas o operador precisa saber que existe
                   um grupo — é o que diferencia o "Adicionar outro" que soma do
                   que abre cobrança nova. */
                <p className="w-full text-xs leading-relaxed text-base-content/60">
                    Cobrança #{billingTargetId} em aberto
                    {groupedCount > 1
                        ? `, com ${groupedCount} atendimentos`
                        : ''}
                    . "Adicionar outro" soma o próximo atendimento nela.
                </p>
            ) : (
                <p className="w-full text-xs leading-relaxed text-base-content/50">
                    {addressConfirmed
                        ? 'Tudo pronto para salvar.'
                        : 'Confirme o local de atendimento para liberar o salvamento.'}
                </p>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                    type="button"
                    onClick={onCancel}
                    className="btn btn-ghost sm:w-36"
                    disabled={loading}
                >
                    Cancelar
                </button>
                {/* Mesma visita, vários atendimentos: salva este e volta ao
                    formulário limpo, já apontando a cobrança que nasceu — o
                    próximo é somado nela, e o paciente paga uma vez só. Sem isto,
                    cada atendimento vira uma cobrança e um link.

                    O botão faz DUAS coisas: "salva e já agenda outro para este
                    paciente" e "soma na mesma cobrança". A segunda depende de a
                    cobrança aceitar soma (em aberto, de hoje, sem forma de
                    pagamento nem pagamento gerado — ver
                    `aplicarCobrancaDoRegistro`); a primeira vale sempre. Por isso
                    o botão aparece mesmo com a cobrança já paga: o próximo
                    atendimento apenas nasce com cobrança própria, em vez de
                    engordar uma que o paciente já quitou. */}
                {!isBudgetMode && !isTeleconsultation && (
                    <button
                        type="submit"
                        onClick={() => onAddAnotherToggle(true)}
                        // `min-w` e não `w`: "Salvar e agendar outro" não cabe na
                        // largura fixa e quebraria em duas linhas.
                        className="btn btn-outline gap-2 sm:min-w-44"
                        disabled={loading || !addressConfirmed}
                        title={
                            !isEditing
                                ? `Salva este e continua no mesmo ${clientLabel}, somando tudo numa cobrança só`
                                : billingTargetId != null
                                  ? `Salva esta edição e abre um novo atendimento do mesmo ${clientLabel}, somando na mesma cobrança`
                                  : `Salva esta edição e abre um novo atendimento do mesmo ${clientLabel} — a cobrança deste já foi fechada, então o novo terá cobrança própria`
                        }
                    >
                        <MdAdd className="text-lg" />
                        {/* O rótulo diz o que VAI acontecer. "Adicionar outro"
                            promete grupo; sem cobrança que aceite soma, o próximo
                            atendimento tem cobrança própria, e chamar isso de
                            "adicionar" faria o operador acreditar que somou num
                            pagamento já fechado. */}
                        {isEditing && billingTargetId == null
                            ? 'Salvar e agendar outro'
                            : 'Adicionar outro'}
                    </button>
                )}
                <button
                    type="submit"
                    // Se "Adicionar outro" foi clicado antes e o envio parou numa
                    // validação, a marca continua ligada: desligar aqui evita este
                    // clique cair no fluxo errado.
                    onClick={() => onAddAnotherToggle(false)}
                    className="btn btn-secondary gap-2 sm:w-44"
                    disabled={loading || !addressConfirmed}
                    title={
                        !addressConfirmed
                            ? 'Confirme o local de atendimento antes de agendar'
                            : grouping && !isEditing
                              ? 'Salva este e vai ao pagamento de todos os atendimentos desta cobrança'
                              : undefined
                    }
                >
                    {loading ? (
                        <>
                            <span className="loading loading-spinner loading-sm"></span>
                            Salvando...
                        </>
                    ) : (
                        <>
                            <MdSave className="text-lg" />
                            {isEditing
                                ? isBudgetMode
                                    ? 'Salvar orçamento'
                                    : 'Salvar alterações'
                                : isBudgetMode
                                  ? 'Criar orçamento'
                                  : grouping
                                    ? 'Finalizar'
                                    : 'Agendar'}
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}
